<template>
  <div class="payment-container">
    <div class="payment-flow">

      <h1 class="main-title">Suscripción Informe Semanal Eurekers</h1>

      <!-- Progress Indicator - Now only 2 stages -->
      <div class="progress-indicator">
        <div v-for="stage in 2" :key="stage"
          :class="['circle', { active: currentStage === stage, completed: currentStage > stage }]">
          {{ stage }}
        </div>
      </div>

      <!-- Stage 1: Cuenta IBAN (was Stage 2) -->
      <div v-if="currentStage === 1" class="stage stage-1">
        <h2>Cuenta IBAN</h2>
        <form @submit.prevent="nextStage">
          <div class="form-group">
            <SpanishIbanInput v-model="formData.iban" placeholder="IBAN (ES + 22 dígitos)" />
            <span class="error-message" v-if="errors.iban">{{ errors.iban }}</span>
          </div>
          
          <div class="info-container">
            <p class="info-text">
              Acepto el cargo de 120€ + IVA por el acceso al Informe Semanal de Eurekers durante 12 meses. Este importe será cargado mediante domiciliación bancaria en la cuenta IBAN proporcionada. La suscripción se renovará automáticamente al finalizar el periodo, salvo notificación previa de cancelación.
            </p>
          </div>
          
          <div class="terms-container">
            <div class="checkbox-container">
              <input type="checkbox" id="terms" v-model="formData.termsAccepted" />
              <label for="terms" class="terms-text">
                Acepto estos términos y condiciones
              </label>
            </div>
          </div>
          <span class="error-message" v-if="errors.terms">{{ errors.terms }}</span>
          <span class="error-message" v-if="errors.stage1">{{ errors.stage1 }}</span>
          
          <div class="navigation-buttons stage1-nav">
            <!-- No "Atrás" button needed for the first stage -->
            <button type="submit" class="primary-btn" :disabled="loading.submitForm || !formData.iban || formData.iban.length < 24">
              {{ loading.submitForm ? 'Procesando...' : 'Siguiente' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Stage 2: Completado (was Stage 3) -->
      <div v-if="currentStage === 2" class="stage stage-2">
        <h2>{{ errors.stage2 ? 'Ha ocurrido un error' : '¡Completado!' }}</h2>
        <div class="success-container" :class="{ 'error-container': errors.stage2 }">
          <div class="success-icon" v-if="!errors.stage2">
            <i class="material-icons">check_circle</i>
          </div>
          <div class="error-icon" v-if="errors.stage2">
            <i class="material-icons">error</i>
          </div>
          <p v-if="!errors.stage2">Te has suscrito al informe correctamente. El primer cobro puede tardar unos días.</p>
          <p v-if="errors.stage2">{{ errors.stage2 }}</p>
        </div>
        <div class="navigation-buttons centered">
          <button class="primary-btn highlight-btn" @click="resetFlow">
            {{ errors.stage2 ? 'Volver a intentar' : 'Ir al informe' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SpanishIbanInput from '@/shared/components/common/SpanishIbanInput.vue';

export default {
  name: 'InformeSubscribe',
  components: {
    SpanishIbanInput
  },
  data() {
    return {
      currentStage: 1, // Start directly at IBAN stage
      formData: {
        iban: '',
        termsAccepted: false
      },
      validations: {
        ibanValid: false
      },
      errors: {
        iban: '',
        stage1: '',
        terms: '',
        stage2: ''
      },
      loading: {
        submitForm: false
      }
    }
  },
  methods: {
    validateIbanChecksum(iban) {
      // Spanish IBAN must be 24 characters starting with ES
      if (!iban || iban.length !== 24 || !iban.startsWith('ES')) {
        return false;
      }

      // Move first 4 characters to the end
      const rearranged = iban.slice(4) + iban.slice(0, 4);
      
      // Replace letters with numbers (A=10, B=11, ..., Z=35)
      let numericString = '';
      for (let char of rearranged) {
        if (char >= '0' && char <= '9') {
          numericString += char;
        } else {
          numericString += (char.charCodeAt(0) - 55).toString();
        }
      }
      
      // Calculate mod 97
      let remainder = 0;
      for (let i = 0; i < numericString.length; i++) {
        remainder = (remainder * 10 + parseInt(numericString[i])) % 97;
      }
      
      return remainder === 1;
    },

    validateIBAN() {
      this.errors.iban = '';
      this.validations.ibanValid = false;

      if (!this.formData.iban || this.formData.iban.length < 24) {
        this.errors.iban = 'El IBAN debe contener ES seguido de 22 dígitos';
        return false;
      }

      if (this.formData.iban.length !== 24 || !this.formData.iban.startsWith('ES')) {
        this.errors.iban = 'Formato de IBAN español inválido (ES + 22 dígitos)';
        return false;
      }

      if (!this.validateIbanChecksum(this.formData.iban)) {
        this.errors.iban = 'El IBAN introducido no es válido. Por favor, verifica los dígitos de control';
        return false;
      }

      this.validations.ibanValid = true;
      return true;
    },

    async nextStage() {
      if (this.currentStage === 1) {
        this.errors.stage1 = '';
        
        // Validate IBAN
        if (!this.validateIBAN()) {
          return;
        }

        if (!this.formData.termsAccepted) {
          this.errors.terms = 'Debe aceptar los términos y condiciones para continuar';
          return;
        } else {
          this.errors.terms = '';
        }

        try {
          this.loading.submitForm = true;
          // API call now only sends IBAN
          const response = await fetch('/api/informe/add-consumable', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              iban: this.formData.iban
              // Email and codes removed
            })
          });

          let isSuccess = true;
          let errorMessage = '';

          if (!response.ok) {
            const errorText = await response.text();
            errorMessage = 'Error al procesar la suscripción';
            try {
              const errorData = JSON.parse(errorText);
              if (errorData.error) errorMessage = errorData.error;
            } catch (e) {
              if (errorText) errorMessage = errorText;
            }
            isSuccess = false;
          } else {
            const data = await response.json();
            if (!data.success) {
              errorMessage = data.error || 'Ha ocurrido un error al procesar la suscripción';
              isSuccess = false;
            }
          }

          this.currentStage = 2; // Move to completion stage
          
          if (!isSuccess) {
            this.errors.stage2 = errorMessage || 'Error al procesar la suscripción. Por favor, inténtelo de nuevo o contacte con soporte.';
          } else {
            this.errors.stage2 = ''; // Clear error if successful
          }
          
          return; // Important: return after handling stage 1 logic
        } catch (error) {
          console.error('Error:', error);
          this.currentStage = 2; // Move to completion stage even on error
          this.errors.stage2 = error.message || 'Error al procesar la suscripción. Por favor, inténtelo de nuevo o contacte con soporte.';
          return; // Important: return after handling stage 1 logic
        } finally {
          this.loading.submitForm = false;
        }
      }

      // This part should not be reached if stage 1 returns, but kept as safeguard
      // if (this.currentStage < 2) { 
      //   this.currentStage++;
      // }
    },

    // Removed sendEmailCode, validateEmailCode, sendPhoneCode, validatePhoneCode

    // previousStage is removed as there's no stage before IBAN now

    resetFlow() {
       // If there was an error, stay on page, otherwise go to informes
      if (!this.errors.stage2) {
        this.$router.push('/app/informes');
      } else {
         // Reset to stage 1 to allow retry
         this.currentStage = 1;
         this.formData.iban = '';
         this.formData.termsAccepted = false;
         this.validations.ibanValid = false;
         this.errors.iban = '';
         this.errors.stage1 = '';
         this.errors.terms = '';
         this.errors.stage2 = ''; // Clear completion stage error on retry
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.payment-container {
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
}

.payment-flow {
  max-width: 600px;
  width: 100%;
  padding: 25px;
  background-color: var(--color-surface);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: var(--color-text-primary);
}

h2 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 25px;
  text-align: center;
  color: var(--color-text-primary);
}

.progress-indicator {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  position: relative;
  max-width: 200px;
  margin-left: auto;
  margin-right: auto;
  
  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background-color: var(--color-muted);
    z-index: 1;
    transform: translateY(-50%);
  }
}

.circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--color-muted);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  margin-top: 0 !important;
  
  &.active {
    background-color: var(--primary);
    box-shadow: 0 0 0 4px rgba(0, 174, 221, 0.2);
    transform: scale(1.1);
  }
  
  &.completed {
    background-color: var(--success);
  }
}

.stage {
  display: flex;
  flex-direction: column;
  gap: 15px;
  transition: all 0.3s ease;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: var(--color-background);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

input {
  padding: 12px 15px;
  border: 1px solid var(--color-outline);
  border-radius: 8px;
  font-size: 14px;
  background-color: var(--color-background);
  color: var(--color-text-primary);
  transition: border-color 0.3s;
  margin-bottom: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 174, 221, 0.1);
  }
  
  &::placeholder {
    color: var(--color-text-inactive);
  }
}

button {
  padding: 12px 15px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.primary-btn {
    background-color: var(--primary);
    color: var(--color-text-on-highlight);
    
    &:hover {
      background-color: #0098c4;
    }
  }
  
  &.secondary-btn {
    background-color: var(--color-active-surface);
    color: var(--color-text-primary);
    
    &:hover {
      background-color: #223a4a;
    }
  }
  
  &:disabled {
    background-color: var(--color-muted);
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.verification-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: var(--color-background);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  
  &.stage1-nav {
    justify-content: flex-end;
  }
  
  &.centered {
    justify-content: center;
  }
  
  button {
    min-width: 120px;
  }
}

.error-message {
  color: var(--error);
  font-size: 13px;
  font-weight: 500;
  margin-top: 5px;
}

.success-message {
  color: var(--success);
  font-size: 13px;
  font-weight: 500;
  margin-top: 5px;
}

.success-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: rgba(67, 160, 71, 0.1);
  padding: 30px;
  border-radius: 8px;
  margin: 20px 0;
  
  .success-icon {
    font-size: 64px;
    color: var(--success);
    margin-bottom: 20px;
    
    i {
      font-size: 64px;
    }
  }
  
  p {
    font-size: 16px;
    text-align: center;
    line-height: 1.5;
  }
}

@media (max-width: 767px) {
  .payment-flow {
    margin: 20px;
    padding: 20px;
  }
  
  .navigation-buttons button {
    min-width: 100px;
  }
}

.main-title {
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 25px;
  color: var(--primary);
}

.terms-container {
  margin-top: 15px;
}

.checkbox-container {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.terms-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.info-container {
  background-color: rgba(0, 174, 221, 0.1);
  border-radius: 8px;
  padding: 15px;
  margin: 10px 0;
}

.info-text {
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  margin: 0;
}

.highlight-btn {
  font-size: 18px;
  font-weight: 600;
  padding: 15px 30px;
  min-width: 200px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 174, 221, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 174, 221, 0.4);
  }
}

.error-container {
  background-color: rgba(244, 67, 54, 0.1) !important;
  
  .error-icon {
    font-size: 64px;
    color: var(--error);
    margin-bottom: 20px;
    
    i {
      font-size: 64px;
    }
  }
}
</style>
